/**
 * Boli Live - Cart Drawer Component
 *
 * Slide-over cart drawer for quick cart management without leaving the page.
 */

import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCartStore } from '../lib/stores/cart-store';
import { formatPKR, cn } from '../lib/utils';
import type { CartItem } from '@boli/types';

export function CartDrawer() {
  const {
    cart,
    isLoading,
    isUpdating,
    isDrawerOpen,
    closeDrawer,
    updateItemQuantity,
    removeItem,
    clearCart,
  } = useCartStore();

  const items = cart?.items || [];
  const isEmpty = items.length === 0;

  return (
    <AnimatePresence>
      {isDrawerOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeDrawer}
            className="fixed inset-0 bg-black/60 z-[100] backdrop-blur-sm"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-background shadow-2xl z-[101] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-foreground">Your Cart</h2>
                {!isEmpty && (
                  <span className="bg-primary text-primary-foreground text-xs font-bold px-2 py-0.5 rounded-full">
                    {cart?.itemCount || 0}
                  </span>
                )}
              </div>
              <button
                onClick={closeDrawer}
                className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 space-y-4">
                  {[1, 2, 3].map((i) => (
                    <CartItemSkeleton key={i} />
                  ))}
                </div>
              ) : isEmpty ? (
                <div className="flex flex-col items-center justify-center h-full py-12 text-center px-4">
                  <ShoppingCart className="w-16 h-16 text-muted-foreground/30 mb-4" />
                  <p className="text-lg font-medium text-foreground mb-1">Your cart is empty</p>
                  <p className="text-sm text-muted-foreground mb-6">
                    Add some products to get started!
                  </p>
                  <button
                    onClick={closeDrawer}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  {items.map((item: CartItem) => (
                    <CartItemCard
                      key={item.id}
                      item={item}
                      isUpdating={isUpdating}
                      onUpdateQuantity={(qty) => updateItemQuantity(item.id, qty)}
                      onRemove={() => removeItem(item.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {!isEmpty && (
              <div className="border-t border-border p-4 space-y-4 bg-card">
                {/* Totals */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span>{formatPKR(cart?.subtotal || 0)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Shipping</span>
                    <span>{formatPKR(cart?.shippingTotal || 0)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Tax (PST)</span>
                    <span>{formatPKR(cart?.pstTotal || 0)}</span>
                  </div>
                  <div className="flex justify-between text-foreground font-bold text-base pt-2 border-t border-border">
                    <span>Total</span>
                    <span>{formatPKR(cart?.grandTotal || 0)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <Link
                    to="/cart"
                    onClick={closeDrawer}
                    className="w-full py-3 bg-primary text-primary-foreground rounded-md text-sm font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
                  >
                    View Cart & Checkout
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => clearCart()}
                    disabled={isUpdating}
                    className="w-full py-2 text-red-600 hover:text-red-700 text-sm font-medium flex items-center justify-center gap-1 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Clear Cart
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Cart Item Card
interface CartItemCardProps {
  item: {
    id: string;
    productId: string;
    quantity: number;
    product?: {
      id: string;
      title: string;
      price: number;
      images?: string[];
      stock: number;
    };
  };
  isUpdating: boolean;
  onUpdateQuantity: (quantity: number) => void;
  onRemove: () => void;
}

function CartItemCard({ item, isUpdating, onUpdateQuantity, onRemove }: CartItemCardProps) {
  const product = item.product;
  const maxQuantity = product?.stock || 10;

  return (
    <div className="flex gap-3 p-3 bg-card border border-border rounded-lg">
      {/* Image */}
      <Link to={`/product/${item.productId}`} className="flex-shrink-0">
        <div className="w-20 h-20 rounded-md overflow-hidden bg-secondary">
          <img
            src={product?.images?.[0] || '/placeholder-product.jpg'}
            alt={product?.title || 'Product'}
            className="w-full h-full object-cover"
          />
        </div>
      </Link>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <Link to={`/product/${item.productId}`}>
          <h3 className="text-sm font-medium text-foreground line-clamp-2 hover:underline">
            {product?.title || 'Loading...'}
          </h3>
        </Link>

        <p className="text-sm font-bold text-foreground mt-1">
          {product ? formatPKR(product.price) : '...'}
        </p>

        {/* Quantity Controls */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1">
            <button
              onClick={() => onUpdateQuantity(item.quantity - 1)}
              disabled={isUpdating || item.quantity <= 1}
              className={cn(
                'w-7 h-7 rounded-md flex items-center justify-center transition-colors',
                'border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              <Minus className="w-3.5 h-3.5" />
            </button>

            <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>

            <button
              onClick={() => onUpdateQuantity(item.quantity + 1)}
              disabled={isUpdating || item.quantity >= maxQuantity}
              className={cn(
                'w-7 h-7 rounded-md flex items-center justify-center transition-colors',
                'border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Remove */}
          <button
            onClick={onRemove}
            disabled={isUpdating}
            className="p-1.5 text-muted-foreground hover:text-red-600 transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Skeleton
function CartItemSkeleton() {
  return (
    <div className="flex gap-3 p-3 bg-card border border-border rounded-lg">
      <div className="w-20 h-20 rounded-md bg-muted animate-pulse" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-muted rounded w-3/4 animate-pulse" />
        <div className="h-4 bg-muted rounded w-1/3 animate-pulse" />
        <div className="h-7 bg-muted rounded w-24 animate-pulse mt-2" />
      </div>
    </div>
  );
}
